import type { Workbook, Worksheet } from "exceljs";
import { applyWrapText } from "../../excelWrapText";
import type { WeekBlock, WeekStaffScheduleExportColors, WeekStaffScheduleExportLabels, WeekStaffScheduleExportPageSetup } from "../types";
import { computeWeekColumnWidths } from "./columnWidths";
import { renderExportHeader } from "./exportHeader";
import { applyWeekSchedulePageSetup } from "./pageSetup";
import { renderWeekBlock } from "./weekBlock";

type RenderWeekScheduleSheetArgs = {
	workbook: Workbook;
	blocks: WeekBlock[];
	labels: WeekStaffScheduleExportLabels;
	colors: WeekStaffScheduleExportColors;
	pageSetup: WeekStaffScheduleExportPageSetup;
	columnWidthOverrides: Record<string, number> | undefined;
};

/**
 * Render every week onto a single worksheet.
 *
 * One sheet rather than one per period: the PDF renderer emits a separate page and table per sheet,
 * and the in-app preview hides sheet tabs — so extra sheets would be invisible and paginate worse.
 */
export const renderWeekScheduleSheet = ({ workbook, blocks, labels, colors, pageSetup, columnWidthOverrides }: RenderWeekScheduleSheetArgs): Worksheet => {
	const worksheet = workbook.addWorksheet(labels.sheetName);
	worksheet.columns = computeWeekColumnWidths(columnWidthOverrides);
	applyWeekSchedulePageSetup({ worksheet, title: labels.title, pageSetup });

	let currentRow = renderExportHeader({ worksheet, labels, colors });
	for (const block of blocks) {
		currentRow = renderWeekBlock({ worksheet, block, startRow: currentRow, colors });
	}

	// Last, so it also covers the header and date-row cells the block renderers do not wrap. Entry rows
	// are already sized to their stacked lines; the pass only grows a row whose text needs more.
	applyWrapText(worksheet);

	return worksheet;
};
